import { TableTextFilter } from "components/TableTextFilter/TableTextFilter";
import { setScrollBehavior } from "features/scrollPageBehaviorProvider/scrollPageBehaviorProvider";
import { _ } from "i18n";
import {DiscoverPlayerDataResponse, DiscoverPlayerResponse} from "libs/apiClient";
import client from "libs/client";
import { useEffect, useState } from "react";
import DatagridV2, { type SimpleColumn } from "ui-kit/src/Datagrid/DatagridV2";
import SelectComponent from "ui-kit/src/SelectComponent/SelectComponent";
import { PositionChoices, BooleanChoices } from "features/discover/types";
import {createSortArrows} from "features/discover/functions";
import { renderBoolean } from "features/datagrid-configs/cellRenderers";

const entityTypeOptions = [
	{
		label: 'Discover',
		value: 'Discover',
	},
	{
		label: 'Partner',
		value: 'Partner',
	},
	{
		label: 'NonPartner',
		value: 'NonPartner',
	},
];

const genderOptions = [
	{
		value: "Male",
		label: "Male",
	},
	{
		value: "Female",
		label: "Female",
	}
];

export function DiscoverPlayersDataTable({
	columnVisibility,
	defaultFilters = {},
	rowAction = undefined,
}: {
	columnVisibility?: Record<string, boolean>;
	defaultFilters?: { [key: string]: string | number | undefined };
	rowAction?: (row) => void;
}) {
    const [pagination, setPagination] = useState<{ pageIndex: number; pageSize: number }>({
        pageIndex: 0,
        pageSize: 15,
    });

    const [filters, setFilters] = useState<{ [key: string]: string | number | undefined }>({ ...defaultFilters});
    const [filterValues, setFilterValues] = useState<{ [key: string]: string | number | undefined }>({ ...defaultFilters});
    const [sort, setSort] = useState<{ field: string, direction: string }>();

    useEffect(() => { setScrollBehavior() }, []);

    const { data, isLoading } = client.getDiscoverPlayersData.useQuery({
        page: pagination.pageIndex + 1,
		limit: pagination.pageSize,
		filters: [
            ...(filters?.id ? [{
                field: "id",
                type: "search",
                value: filters.id,
            }] : []),
            ...(filters?.firstName ? [{
                field: "firstName",
                type: "search",
                value: filters.firstName,
            }] : []),
            ...(filters?.lastName ? [{
                field: "lastName",
                type: "search",
                value: filters.lastName,
            }] : []),
            ...(filters?.membershipName ? [{
                field: "membershipName",
                type: "search",
                value: filters.membershipName,
            }] : []),
            ...(filters?.club ? [{
                field: "club",
                type: "search",
                value: filters.club,
            }] : []),
            ...(filters?.team ? [{
                field: "team",
                type: "search",
                value: filters.team,
            }] : []),
            ...(filters?.playerType ? [{
                field: "playerType",
                type: "exact",
                value: filters.playerType,
            }] : []),
            ...(filters?.clubType ? [{
                field: "clubType",
                type: "exact",
                value: filters.clubType,
            }] : []),
            ...(filters?.gender ? [{
                field: "gender",
                type: "exact",
                value: filters.gender,
            }] : []),
            ...(filters?.position ? [{
                field: "position",
                type: "exact",
                value: filters.position,
            }] : []),
            ...(filters?.isVip ? [{
                field: "isVip",
                type: "boolean",
                value: filters.isVip === "true",
            }] : []),
            ...(filters?.numberSign ? [{
                field: "numberSign",
                type: "search",
                value: filters.numberSign,
            }] : []),
        ],
        order: sort
    })

    const handleSetFilter = (key: string, value?: number | string,) => {
		const newFilters = { ...filters, [key]: value }
        setFilters(newFilters);
        setPagination({ ...pagination, pageIndex: 0 });
    }

    const handleClearFilter = (key: string) => {
        setFilters({ ...filters, [key]: undefined })
    }

	const handleSort = (newSort?: {field: string, direction: string}) => {
		setSort(newSort);
	}

    const generateColumnHeader = (columnName: string, label: string, hasSort: boolean = true, className?: string) => () => (
        <div className={`tableHeader ${className}`}>
            <TableTextFilter
                value={(filterValues[columnName] || "").toString()}
                onChange={(value) => setFilterValues({ ...filterValues, [columnName]: value })}
                clear={() => {
                    handleClearFilter(columnName);
                    setFilterValues({ ...filterValues, [columnName]: undefined })
                }}
                onSubmit={(value: string) => handleSetFilter(columnName, value)}
                placeholder={label}
            />
            {hasSort ? createSortArrows(columnName, sort, handleSort) : ''}
        </div>
    );

    const simpleColumns: SimpleColumn<DiscoverPlayerDataResponse>[] = [
        {
            id: "id",
            key: "id",
            header: generateColumnHeader("id", "ID", true, "tableHeader--id"),
        },
		{
			id: "firstName",
			key: "firstName",
			header: generateColumnHeader("firstName", "First name"),
			cell: (value, row) => row.firstName ?? "-",
		},
		{
			id: "lastName",
			key: "lastName",
			header: generateColumnHeader("lastName", "Last name"),
			cell: (value, row) => row.lastName ?? "-",
		},
		{
			id: "height",
			key: "height",
			header: "height",
			cell: value => <div className="thinCell">{value ? value + ' cm' : '-'}</div>
		},
		{
			id: "weight",
			key: "weight",
			header: "Weight",
			cell: value => <div className="thinCell">{value ? value + ' kg' : '-'}</div>
		},
		{
			id: "gender",
			key: "gender",
			header: () => <SelectComponent
				value={genderOptions.find((o) => o.value === filters?.gender)}
				handleChange={(value) => {
					handleSetFilter("gender", value?.value)
				}}
				placeholder="Gender"
				items={genderOptions}
				isCleareable
			/>,
			cell: (value) => <div className="thinCell">{value as string | null || '-'}</div>
		},
		{
			id: "membershipName",
			key: "membershipName",
			header: generateColumnHeader("membershipName", "Membership Name", false),
			cell: (value, row) => {
				return row.players?.map((player: DiscoverPlayerResponse) => (
					<div style={player.fullName !== row.fullName ? { color: "red" } : {}} key={player.id}>{player.fullName}</div>
				))
			},
		},
		{
			id: "playerType",
			key: "playerType",
			header: () => <div className="tableHeader tableHeader--select">
				<SelectComponent
					value={entityTypeOptions.find((o) => o.value === filters?.playerType)}
					handleChange={(value) => {
						handleSetFilter("playerType", value?.value)
					}}
					placeholder="Player type"
					items={entityTypeOptions}
				/>
			</div>,
			cell: (value, row) => {
				return row.players?.map((player: DiscoverPlayerResponse) => (
					<div className="thinCell" key={player.id}>{player.playerType}</div>
				))
			},
		},
		{
			id: "club",
			key: "club",
			header: generateColumnHeader("club", "Club", false),
			cell: (value, row) => {
				return row.players?.map((player: DiscoverPlayerResponse) => (
					<div key={player.id}>{player.club?.name || '-'}</div>
				))
			},
		},
		{
			id: "clubType",
			key: "clubType",
			header: () => <div className="tableHeader tableHeader--select">
				<SelectComponent
					value={entityTypeOptions.find((o) => o.value === filters?.clubType)}
					handleChange={(value) => {
						handleSetFilter("clubType", value?.value)
					}}
					placeholder="Club type"
					items={entityTypeOptions}
				/>
			</div>,
			cell: (value, row) => {
				return row.players?.map((player: DiscoverPlayerResponse) => (
					<div className="thinCell" key={player.id}>{player.club?.metadata["type"] || '-'}</div>
				))
			},
		},
		{
			id: "team",
			key: "team",
			header: generateColumnHeader("team", "Team", false),
			cell: (value, row) => {
				return row.players?.map((player: DiscoverPlayerResponse) => (
					<div key={player.id}>{player.team?.name || '-'}</div>
				))
			},
		},
		{
			id: "position",
			key: "position",
			header: () => <SelectComponent
				value={PositionChoices.find((o) => o.value === filters.position)}
				handleChange={(value) => {
					handleSetFilter("position", value?.value)
				}}
				placeholder="Position"
				items={PositionChoices}
				isCleareable
			/>,
			cell: (value, row) => {
				return row.players?.map((player: DiscoverPlayerResponse) => (
					<div key={player.id}>{player.position || '-'}</div>
				))
			},
		},
		{
			id: "numberSign",
			key: "numberSign",
			header: "Number sign",
			cell: (value, row) => {
				return row.players?.map((player: DiscoverPlayerResponse) => (
					<div key={player.id}>{player.numberSign || '-'}</div>
				))
			},
		},
		{
			id: "isVip",
			key: "isVip",
			header: () => <SelectComponent
				value={BooleanChoices.find((o) => o.value === filters?.isVip)}
				handleChange={(value) => {
					handleSetFilter("isVip", value?.value)
				}}
				placeholder="Vip"
				items={BooleanChoices}
				isCleareable
			/>,
			cell: (_, row) => {
				return row.players?.map((player: DiscoverPlayerResponse) => (
					<div className="thinCell" key={player.id}>{renderBoolean(player.isVip)}</div>
				))
			},
		},
	];

	return (
		<DatagridV2
			rowAction={rowAction}
			getRowLink={rowAction ? undefined : (row) => `/discover/player-data/${row.id}`}
			isLoading={isLoading}
			simpleColumns={simpleColumns}
			data={data?.data.items ?? []}
			pageCount={data?.data?.pages ?? 1}
			pagination={pagination}
			setPagination={setPagination}
			noResults={_("datatables.zero_records")}
			columnVisibility={columnVisibility}
		/>
	)
}
